import React from "react";
import { Heading, Box, Text } from "theme-ui";
import { Decimal, Percent, LiquityStoreState, CRITICAL_COLLATERAL_RATIO, CollateralBaseState, CollateralDerivedState } from "@liquity/lib-base";
import { useLiquitySelector } from "@liquity/lib-react";

import { Statistic } from "./PSY/Statistic";
import * as l from "../lexicon";
import { getTotalCollateralRatioStyle } from '../utils/helper'
import { merge } from 'lodash'
import { StatusBadge } from "./PSY/Badge"

const selectBalances = ({ collateralStates, slsdBalance, psyBalance }: LiquityStoreState) => {

  const balanceList = Object.entries(collateralStates).reduce<{ [collateralKey: string]: Decimal }>((acc, [key, value]) => {
    acc[key] = value.balance
    return acc
  }, {})
  return {
    balanceList,
    slsdBalance,
    psyBalance
  }
};

const Balances: React.FC = () => {
  const { balanceList, slsdBalance, psyBalance } = useLiquitySelector(selectBalances);

  return (
    <Box sx={{ mb: 3 }}>
      <Heading>My Account Balances</Heading>
      {Object.entries(balanceList).map(([key, balance], i) => (
        <Statistic lexicon={key} key={i}>{balance.prettify(4)}</Statistic>
      ))}
      <Statistic lexicon={l.SLSD}>{slsdBalance.prettify()}</Statistic>
      <Statistic lexicon={l.PSY}>{psyBalance.prettify()}</Statistic>
    </Box>
  );
};


type SystemStatsProps = {
  variant?: string;
  showBalances?: boolean;
};

type ResultSelector = Pick<
  CollateralBaseState & CollateralDerivedState,
   "numberOfTroves" | "price" | "total" | "slsdInStabilityPool" | "borrowingRate"
  > & {
    slsdInStabilityPoolPct: Percent<Decimal, { gte(n: string): boolean }> | undefined,
    totalCollateralRatioPct: Percent<Decimal, { gte(n: string): boolean }>,
    tcrStyle: string,
    isRecoveryMode: boolean
  }

const select = ({ collateralStates, collateralDerivedStates }: LiquityStoreState) => {
  const states = merge(collateralStates, collateralDerivedStates)
  const selected = Object.entries(states).reduce<{ [collateralKey: string]: ResultSelector }>((acc, [key, value]) => {
    const { numberOfTroves, price, total, slsdInStabilityPool, borrowingRate } = value
    const slsdInStabilityPoolPct =
      total.debt.nonZero && new Percent(slsdInStabilityPool.div(total.debt));
    const totalCollateralRatioPct = new Percent(total.collateralRatio(price));
    // const borrowingFeePct = new Percent(borrowingRate);

    const tcrStyle = getTotalCollateralRatioStyle(total.collateralRatio(price))
    const isRecoveryMode = total.collateralRatio(price).lt(CRITICAL_COLLATERAL_RATIO)

    acc[key] = { numberOfTroves, price, total, slsdInStabilityPool, borrowingRate, slsdInStabilityPoolPct, totalCollateralRatioPct, tcrStyle, isRecoveryMode }
    return acc
  }, {})
  return { ...selected }
};

export const SystemStats: React.FC<SystemStatsProps> = ({ variant = "info", showBalances }) => {
  const selected = useLiquitySelector(select);
  const collateralStatsList = Object.entries(selected).map(([key, stats]) => {
    const lexicon = l.CollateralLexicons.find(lexicon => lexicon.key === key)
    return { key, lexicon, ...stats }
  })

  return (
    <div className="bg-component border-component p-5 flex flex-col h-fit">
      {collateralStatsList.map((collateralStats, i) => {
        const { price, total, slsdInStabilityPool, slsdInStabilityPoolPct, totalCollateralRatioPct, tcrStyle, isRecoveryMode, lexicon } = collateralStats
        return (
          <div className="mt-5 border-card bg-card p-4" key={i}>
            <div className="flex flex-row items-center">
              <img src={lexicon?.iconUrl} alt={lexicon?.term} className="w-6 h-6"/>
              <div className="text-style-item ml-3">{lexicon?.name}</div>
            </div>
            <Statistic lexicon={l.TVL}>
              <div className="text-style-item">
                <span className="">{total.collateral.shorten()}</span> &nbsp;{lexicon?.term}
                <span className="">
                  &nbsp;(${Decimal.from(total.collateral.mul(price)).shorten()})
                </span>
              </div>
            </Statistic>
            <Statistic lexicon={l.SLSD_SUPPLY}>{total.debt.shorten()}</Statistic>
            <Statistic lexicon={l.STABILITY_POOL_SLSD}>
              {slsdInStabilityPool.shorten()}
              <Text sx={{ fontSize: 1 }}>&nbsp;({slsdInStabilityPoolPct?.toString(1) ?? '0%'})</Text>
            </Statistic>
            <Statistic lexicon={l.TCR}>
              <span className={tcrStyle}>
                {totalCollateralRatioPct.prettify()}
              </span>
            </Statistic>
            <Statistic lexicon={"Collateral Status"} border={false}>
              {isRecoveryMode ? (
                <StatusBadge status='recoveryMode' />
              ) : (
                <StatusBadge status='normal' />
              )}
            </Statistic>
          </div>
        )
      })}
    </div>
  );
};
